import React, { useState, useEffect } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { connect } from 'containers/store'

import { FieldSection } from 'components/ui/fieldset'
import { Input } from 'components/ui/input'

import { MediaField } from 'domains/media/components/media-field'

import { Media } from 'generated/mos/media'
import { VisitorInfoPage } from 'generated/mos/org/getty/visitorinfo'
import { UpdateVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfomanagement'
import { RadioInputElement } from 'components/ui/radio'
import { Description, Image, Title, ListItemLabel, Subtitle } from 'components/ui/list-item-label'
import styled from 'styled'

import { space } from 'helpers/style'
import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { convertEntityToFormValues } from 'helpers/schema'
import { Status } from 'helpers/status'

import { actionCreators, GettyActionCreators } from '../../../../actions'

import { GettyStore } from '../../../../store'

import { contentPageSchema } from '../../../../schemas/visitorInfo'

import { AmenityCategoryType } from 'generated/mos/org/getty/analytics'

const CATEGORY_TYPE_TITLES = {
  UNSET: '',
  SHOP: 'Shop',
  EAT: 'Eat'
}

const Label = styled.label`
  color: ${({ theme }) => theme.color.grayD60};
  font-weight: ${({ theme }) => theme.font.weight.base};
  margin-bottom: ${space(2)};
  display: inline-block;
`

type FormValues = {
  headerTitle: string | undefined;
  headerImageAltText?: string | undefined;
  descriptionText: string | undefined;
  listTitle: string | undefined;
}

type ActionProps =
  Pick<GettyActionCreators, 'gettyUpdateVisitorInfoPageRequest'>

type ConnectedProps = Pick<GettyStore['getty'], 'visitorInfoUpdate'>

type DirectProps = {
  readonly page: VisitorInfoPage.Entity | undefined;
  readonly setSubmit: (submit: () => void) => void;
  readonly setHasSubmitted: (submitted: boolean) => void;
  readonly setModified: (modifed: boolean | undefined) => void;
}

type Props = ActionProps & ConnectedProps & DirectProps

const ContentPageUpdateView = (props: Props) => {
  const {
    visitorInfoUpdate,
    gettyUpdateVisitorInfoPageRequest,
    page,
    setSubmit,
    setHasSubmitted,
    setModified,
  } = props

  const [ categoryType, setCategoryType ] = useState<AmenityCategoryType>('SHOP')

  const categoryTypeOption = (key: AmenityCategoryType, name: string) => {
    return (
      <ListItemLabel key={key}>
        <RadioInputElement checked={categoryType == key} name={key} onChange={event => { setCategoryType(key) }} />
        <Description>
          <Title>{name}</Title>
        </Description>
      </ListItemLabel>
    );
  }

  // set up form values
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ imageMedia, setImageMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ iconMedia, setIconMedia ] = useState<Media.Ref | undefined>(undefined)

  const pageValues: Partial<VisitorInfoPage.AmenityCategory.Entity & VisitorInfoPage.Entity> = pick({
    ...VisitorInfoPage.AmenityCategory.defaults,
    ...VisitorInfoPage.defaults,
    ...page,
    ...(page && page.pageProperties) ? page.pageProperties: null
  }, 'headerTitle', 'headerImageAltText', 'descriptionText', 'listTitle')

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = page ?
    convertEntityToFormValues<VisitorInfoPage.AmenityCategory.Entity & VisitorInfoPage.Entity | VisitorInfoPage.AmenityCategory.Entity>(pageValues, contentPageSchema) as any as FormValues:
    undefined
  const pageProperties = page && page.pageProperties as VisitorInfoPage.AmenityCategory.Entity
  const initialImageValue = page && pageProperties && contentPageSchema.headerImageRef.read(pageProperties.headerImageRef) as any as Media.Ref | undefined
  const initialIconValue = page && contentPageSchema.listIconRef.read(page.listIconRef) as any as Media.Ref | undefined
  const initialCategoryType = page && pageProperties && pageProperties.categoryType
  if (page && initialCategoryType && initialFormValues && !formBag && visitorInfoUpdate.status === Status.Ready) {
    setFormBag(createFormBag(initialFormValues))
    setImageMedia(initialImageValue)
    setIconMedia(initialIconValue)
    setCategoryType(initialCategoryType)
  }

  useEffect(() => {
    setSubmit(() => () => onSubmit())
  })

  // handle the form ui.
  const modified = initialFormValues && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialImageValue, imageMedia) ||
    !areRefValuesEqual(initialIconValue, iconMedia) ||
    initialCategoryType !== categoryType
  )

  setModified(modified)

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {};

    if (!values.listTitle || !values.listTitle.trim().length) {
      errors.listTitle = 'List title is required'
    }

    return errors
  }

  const onSubmit = () => {
    return formBag && page && submit(formBag, page)
  }

  const submit = (formBag: FormBag<FormValues>, page: VisitorInfoPage.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if(!bag.valid) {
      return false
    }

    const pageProperties = page && page.pageProperties as VisitorInfoPage.AmenityCategory.Entity
    const { listTitle, headerTitle, headerImageAltText, descriptionText} = formBag.values

    setHasSubmitted(true)

    gettyUpdateVisitorInfoPageRequest({
      ...UpdateVisitorInfoPageRequest.defaults,
      visitorInfoPageRef: VisitorInfoPage.mustRef(page.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'listIconRef',
          'listTitle',
          'amenityCategory',
        ]
      },
      listIconRef: contentPageSchema.listIconRef.write(iconMedia) || iconMedia,
      listTitle: contentPageSchema.listTitle.write(listTitle, page.listTitle) || listTitle,
      pageProperties: {
        ...VisitorInfoPage.AmenityCategory.defaults,
        amenityRefs: pageProperties.amenityRefs || [],
        headerTitle: contentPageSchema.headerTitle.write(headerTitle, pageProperties.headerTitle) || headerTitle,
        headerImageAltText: contentPageSchema.headerImageAltText.write(headerImageAltText, pageProperties.headerImageAltText) || headerImageAltText,
        descriptionText: contentPageSchema.descriptionText.write(descriptionText, pageProperties.descriptionText) || descriptionText,
        headerImageRef: contentPageSchema.headerImageRef.write(imageMedia) || imageMedia,
        categoryType: categoryType,
      }
    })
    return false
  }

  return formBag && page && (
    <form noValidate onSubmit={(event) => { event.preventDefault() }}>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
          setSubmit(() => () => onSubmit())
          setModified(modified)
        }}
        validate={() => ({})}
      >
        <Input
          label="Title"
          field="headerTitle"
          error={formBag.touched.headerTitle ? formBag.errors.headerTitle : undefined}
        />
        <MediaField
          kind="MEDIA_KIND_RASTER_IMAGE"
          mediaRef={imageMedia}
          onUpdate={(entity) => { setImageMedia(entity ? entity.ref : undefined) }}
          label="Image"
        />
        <Input
          label="Alternate Text"
          description="Description of what the image contains if it cannot be shown."
          field="headerImageAltText"
          isAssociated={true}
        />
        <Input
          component="textarea"
          label="Description"
          field="descriptionText"
        />
        <FieldSection />
        <Input
          label="List title"
          field="listTitle"
          error={formBag.touched.listTitle ? formBag.errors.listTitle : undefined}
          description="The list title is the page's name in the navigation menu."
        />
        <MediaField
          kind="MEDIA_KIND_RASTER_IMAGE"
          mediaRef={iconMedia}
          onUpdate={(entity) => {
            setIconMedia(entity ? entity.ref : undefined)
            setSubmit(() => () => onSubmit())
          }}
          label="List icon"
        />
        <FieldSection />
        <Label>Analytics category</Label>
        {categoryTypeOption("SHOP", CATEGORY_TYPE_TITLES["SHOP"])}
        {categoryTypeOption("EAT", CATEGORY_TYPE_TITLES["EAT"])}
      </FormData>
    </form>
  )
}

export const ContentPageUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'visitorInfoUpdate'),
  {
    ...pick(actionCreators, 'gettyUpdateVisitorInfoPageRequest'),
  },
)(ContentPageUpdateView)
